import type { Route } from 'next';
import { stripHandlePrefixes } from '@/lib/utils/regex';

export type AppRoute = Route;

export function asRoute(path: string): AppRoute {
  if (!path.startsWith('/')) {
    throw new Error(`Invalid route path: ${path}`);
  }
  // Runtime validation establishes the leading-slash invariant expected by Next's generated route type.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return path as AppRoute;
}

// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
const defineRoute = <Path extends string>(path: Path): AppRoute & Path => asRoute(path) as AppRoute & Path;

type QueryParameterValue = string | number | boolean | null | undefined;

function buildQueryRecord(parameters: Record<string, QueryParameterValue>): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(parameters)) {
    if (value === undefined || value === null) continue;
    const normalized = typeof value === 'string' ? value.trim() : String(value);
    if (normalized.length === 0) continue;
    query[key] = normalized;
  }
  return query;
}

export const ROOT_ROUTE = defineRoute('/');
export const TIMELINE_ROUTE = defineRoute('/timeline');
export const INSIGHTS_ROUTE = defineRoute('/insights');
export const EXPLORE_ROUTE = defineRoute('/explore');
export const FRIENDIMIZER_ROUTE = defineRoute('/friendimizer');
export const PULSE_ROUTE = defineRoute('/pulse');
export const ARCHIVE_TWEETS_ROUTE = defineRoute('/archive/tweets');
export const ARENA_ROUTE = defineRoute('/arena');
export const ADMIN_ROOT_ROUTE = defineRoute('/admin');
export const ADMIN_USERS_ROUTE = defineRoute('/admin/users');
export const ADMIN_TWITTER_LOOKUP_ROUTE = defineRoute('/admin/twitter/lookup');
export const FEATURES_ROUTE = defineRoute('/features');
export const PRICING_ROUTE = defineRoute('/pricing');
export const CONTACT_ROUTE = defineRoute('/contact');
export const LOGIN_ROUTE = defineRoute('/login');
export const AUTH_SIGNIN_ROUTE = defineRoute('/auth/signin');
export const TERMS_ROUTE = defineRoute('/terms');
export const PRIVACY_ROUTE = defineRoute('/privacy');
export const DEBUG_ROUTE = defineRoute('/debug');
export const SEARCH_ROUTE = defineRoute('/search');
export const SETTINGS_ROUTE = defineRoute('/settings');
export const SETTINGS_IMPORT_ROUTE = defineRoute('/settings/import');
export const SETTINGS_ACCOUNT_ROUTE = defineRoute('/settings/account');
export const SETTINGS_SOCIAL_ROUTE = defineRoute('/settings/social');

export function buildSearchRoute(parameters: { query: string; tweetId?: string | null }): AppRoute {
  const query = buildQueryRecord({ q: parameters.query, tweetId: parameters.tweetId });
  const queryString = new URLSearchParams(query).toString();
  return queryString.length > 0 ? `${SEARCH_ROUTE}?${queryString}` : SEARCH_ROUTE;
}

export function buildFriendimizerRoute(parameters: { filter?: string | null } = {}): AppRoute {
  const query = buildQueryRecord({ filter: parameters.filter });
  const queryString = new URLSearchParams(query).toString();
  return queryString.length > 0 ? `${FRIENDIMIZER_ROUTE}?${queryString}` : FRIENDIMIZER_ROUTE;
}

export function buildAdminRoute(parameters: { action?: string | null } = {}): AppRoute {
  const query = buildQueryRecord({ action: parameters.action });
  const queryString = new URLSearchParams(query).toString();
  return queryString.length > 0 ? `${ADMIN_ROOT_ROUTE}?${queryString}` : ADMIN_ROOT_ROUTE;
}

export function buildUserProfileRoute(username: string): AppRoute {
  const handle = stripHandlePrefixes(username.trim());
  return asRoute(`/@${handle}`);
}

export function buildTweetRoute(tweetId: string): AppRoute {
  const id = tweetId.trim();
  return asRoute(`/tweet/${id}`);
}

export function buildAdminTwitterLookupRoute(tweetId?: string): AppRoute {
  if (tweetId === undefined) {
    return ADMIN_TWITTER_LOOKUP_ROUTE;
  }
  return asRoute(`/admin/twitter/lookup/${tweetId.trim()}`);
}
